import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Globe, Trophy, Star, ExternalLink, Newspaper } from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  const h = Math.floor(s / 3600);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function HeadlineRow({ item, testid }) {
  return (
    <a href={item.link} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className="group flex items-start gap-3 px-4 py-3 border-t border-border first:border-t-0 hover:bg-primary/[0.06] transition-colors">
      <ExternalLink size={13} className="mt-1 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
      <div className="min-w-0">
        <p className="text-sm font-medium leading-snug group-hover:text-primary transition-colors">{item.title}</p>
        <p className="mt-0.5 text-[11px] font-data uppercase tracking-wider text-muted-foreground">
          {item.source}{item.source && item.published ? " · " : ""}{relTime(item.published)}
        </p>
      </div>
    </a>
  );
}

function Section({ icon: Icon, title, accent, children, testid }) {
  return (
    <div className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className={accent} />
        <h3 className="font-display text-2xl uppercase tracking-wide">{title}</h3>
      </div>
      <div className="bg-card border border-border">{children}</div>
    </div>
  );
}

export function NewsView({ leagueId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/news?league_id=${leagueId}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [leagueId]);

  if (loading) return <p className="text-center text-sm text-muted-foreground py-10" data-testid="news-loading">Fetching the latest headlines…</p>;
  if (!data) return <p className="text-center text-sm text-muted-foreground py-10">Couldn't load news right now.</p>;

  const empty = (label) => (
    <p className="px-4 py-6 text-center text-xs text-muted-foreground font-data">No {label} headlines right now.</p>
  );

  return (
    <div data-testid="news-view">
      <p className="overline text-primary mb-1">The Wire · live football news</p>
      <p className="text-xs text-muted-foreground font-data mb-6">Updated continuously · tap a headline to read the full story</p>

      <Section icon={Globe} title="Europe" accent="text-primary" testid="news-europe">
        {data.europe?.length
          ? data.europe.map((it, i) => <HeadlineRow key={i} item={it} testid={`news-europe-item-${i}`} />)
          : empty("European")}
      </Section>

      <Section icon={Trophy} title={data.league?.name || "Your league"} accent="text-[hsl(var(--stadium-yellow))]" testid="news-league">
        {data.league?.items?.length
          ? data.league.items.map((it, i) => <HeadlineRow key={i} item={it} testid={`news-league-item-${i}`} />)
          : empty("league")}
      </Section>

      <Section icon={Star} title={data.club?.name || "Your club"} accent="text-[hsl(var(--pitch-green))]" testid="news-club">
        {data.club?.name
          ? (data.club.items?.length
              ? data.club.items.map((it, i) => <HeadlineRow key={i} item={it} testid={`news-club-item-${i}`} />)
              : empty("club"))
          : (
            <div className="px-4 py-8 text-center" data-testid="news-club-prompt">
              <Newspaper size={24} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Pick your favourite club (from the onboarding banner or your first prediction) and
                their headlines will show up here.
              </p>
            </div>
          )}
      </Section>
    </div>
  );
}
